/**
 * Floating Next Button
 *
 * Floating button that appears when sections are affected by AI edits.
 * Shows "Next (n)" where n = remaining affected sections.
 * Clicking approves the current section and scrolls to the next.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Box, Button, Fade } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useEventTracking } from '@services/statsig';
import { useAppDispatch } from '@store/hooks';

import { LAYOUT_Z_INDEX } from '../constants/layout';
import type { UseApprovalNavigationReturn } from '../hooks/useApprovalNavigation';
import { clearAffectedSection, clearSectionSnapshot } from '../store/chatSlice';

interface FloatingNextButtonProps {
  /**
   * Array of affected section IDs
   */
  affectedSectionIds: string[];

  /**
   * Navigation functions and state from useApprovalNavigation hook
   */
  navigation: UseApprovalNavigationReturn;
}

export const FloatingNextButton: React.FC<FloatingNextButtonProps> = ({
  affectedSectionIds,
  navigation,
}) => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const { noteId } = useParams<{ noteId: string; encounterId: string }>();
  const { note: noteMetrics } = useEventTracking();
  const [showDone, setShowDone] = useState(false);

  const { scrollToIndex } = navigation;

  // Track the max count of affected sections seen during this session
  // Only show the button if we had more than 1 section to review
  const maxCountRef = useRef<number>(0);

  const remainingCount = affectedSectionIds.length;
  const isLastSection = remainingCount <= 1;

  // Update max count as sections stream in (count increases)
  // Reset when all sections are approved
  useEffect(() => {
    if (remainingCount > maxCountRef.current) {
      maxCountRef.current = remainingCount;
    }
    if (remainingCount === 0) {
      maxCountRef.current = 0;
    }
  }, [remainingCount]);

  // Only show if we had more than 1 section to review
  const isVisible = remainingCount > 0 && maxCountRef.current > 1;

  const handleApproveAndNext = useCallback(() => {
    if (remainingCount === 0 || !noteId) return;

    const currentSectionId = affectedSectionIds[0]; // Always use first item since we're removing from array

    // Track approval via button (using existing navigation event)
    noteMetrics.approvalNavigationClicked?.(noteId, {
      direction: 'next',
      current_section_index: 0,
      total_sections: remainingCount,
      note_id: noteId,
    });

    // If this was the last section, show "Done!" briefly
    if (isLastSection) {
      // Approve current section (clear affected state)
      dispatch(clearAffectedSection(currentSectionId));
      dispatch(clearSectionSnapshot(currentSectionId));
      setShowDone(true);
      setTimeout(() => {
        setShowDone(false);
      }, 1500);
    } else {
      // Scroll to next section BEFORE approving (next section is at index 1)
      scrollToIndex(1);

      // Then approve current section (this removes it, making the next section become index 0)
      dispatch(clearAffectedSection(currentSectionId));
      dispatch(clearSectionSnapshot(currentSectionId));
    }
  }, [
    remainingCount,
    affectedSectionIds,
    noteId,
    isLastSection,
    scrollToIndex,
    dispatch,
    noteMetrics,
  ]);

  // Always render to prevent layout shift, but hide visually when not visible
  return (
    <Box
      sx={{
        position: 'sticky',
        bottom: 24,
        alignSelf: 'flex-start',
        mt: 'auto',
        ml: '-175px',
        pt: 2,
        zIndex: LAYOUT_Z_INDEX.APPROVAL_BANNER,
        // Hide visually but keep in DOM to prevent layout shift
        visibility: isVisible ? 'visible' : 'hidden',
        opacity: isVisible ? 1 : 0,
        pointerEvents: isVisible ? 'auto' : 'none',
        transition: theme.transitions.create(['opacity', 'visibility'], {
          duration: theme.transitions.duration.short,
        }),
      }}
    >
      <Fade in={!showDone} timeout={300}>
        <Box>
          {showDone ? (
            <Button
              variant="contained"
              color="success"
              startIcon={<CheckCircleIcon />}
              disabled
              sx={{
                minWidth: 125,
                borderRadius: '24px',
                px: 3,
                py: 1.5,
                textTransform: 'none',
                fontWeight: 600,
                boxShadow: theme.shadows[4],
                '&:hover': {
                  boxShadow: theme.shadows[6],
                },
              }}
            >
              Done!
            </Button>
          ) : (
            <Button
              variant="contained"
              color="primary"
              startIcon={isLastSection ? <CheckCircleIcon /> : <ArrowForwardIcon />}
              onClick={handleApproveAndNext}
              sx={{
                minWidth: 125,
                borderRadius: '24px',
                px: 3,
                py: 1.5,
                textTransform: 'none',
                fontWeight: 600,
                boxShadow: theme.shadows[4],
                '&:hover': {
                  boxShadow: theme.shadows[6],
                },
              }}
            >
              {isLastSection ? 'Done!' : `Next (${remainingCount})`}
            </Button>
          )}
        </Box>
      </Fade>
    </Box>
  );
};
